import PropTypes from 'prop-types';
import { m, AnimatePresence } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
// utils
import { fData } from 'src/utils/format-number';
//
import Iconify from '../iconify';
import { varFade } from '../animate';
import FileThumbnail, { fileData } from '../file-thumbnail';

// ----------------------------------------------------------------------

export default function MultiFilePreview({ thumbnail, files, onRemove, onClick, sx }) {
  return (
    <AnimatePresence initial={false}>
      {files?.map((file) => {
        const { key, name = '', size = 0 } = fileData(file);

        const isNotFormatFile = typeof file === 'string';

        if (thumbnail) {
          return (
            <Stack
              key={key}
              component={m.div}
              {...varFade().inUp}
              alignItems="center"
              display="inline-flex"
              justifyContent="center"
              onClick={() => onClick?.(file)}
              sx={{
                m: 0.5,
                width: 80,
                height: 80,
                borderRadius: 1.25,
                overflow: 'hidden',
                position: 'relative',
                border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                cursor: onClick ? 'pointer' : 'default',
                ...sx,
              }}
            >
              <FileThumbnail
                tooltip
                imageView
                file={file}
                sx={{ position: 'absolute' }}
                imgSx={{ position: 'absolute' }}
              />

              {onRemove && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(file);
                  }}
                  sx={{
                    p: 0.5,
                    top: 4,
                    right: 4,
                    position: 'absolute',
                    color: 'common.white',
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                    },
                  }}
                >
                  <Iconify icon="mingcute:close-line" width={14} />
                </IconButton>
              )}

              {file?.location && (
                <Box
                  sx={{
                    p: 0.5,
                    bottom: 4,
                    left: 4,
                    position: 'absolute',
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (theme) => theme.customShadows.z1,
                  }}
                  title={`Location: ${file.location.latitude}, ${file.location.longitude}`}
                >
                  <Iconify icon="solar:map-point-bold" width={12} />
                </Box>
              )}
            </Stack>
          );
        }

        return (
          <Stack
            key={key}
            component={m.div}
            {...varFade().inUp}
            spacing={2}
            direction="row"
            alignItems="center"
            onClick={() => onClick?.(file)}
            sx={{
              my: 1,
              py: 1,
              px: 1.5,
              borderRadius: 1,
              border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
              cursor: onClick ? 'pointer' : 'default',
              '&:hover': {
                bgcolor: onClick ? (theme) => alpha(theme.palette.grey[500], 0.08) : 'transparent',
              },
              ...sx,
            }}
          >
            <FileThumbnail file={file} />

            <ListItemText
              primary={isNotFormatFile ? file : name}
              secondary={
                <Stack spacing={0.5}>
                  <Typography variant="caption" component="span">
                    {isNotFormatFile ? '' : fData(size)}
                  </Typography>
                  {file?.location && (
                    <Typography 
                      variant="caption" 
                      component="span" 
                      sx={{ 
                        color: 'primary.main', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        fontWeight: 'medium'
                      }}
                    >
                      <Iconify icon="solar:map-point-bold" width={12} />
                      {file.location.latitude.toFixed(4)}, {file.location.longitude.toFixed(4)}
                      {file.location.address && ` • ${file.location.address.substring(0, 30)}...`}
                    </Typography>
                  )}
                </Stack>
              }
              secondaryTypographyProps={{
                component: 'div',
              }}
            />

            {onRemove && (
              <IconButton 
                size="small" 
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(file);
                }}
              >
                <Iconify icon="mingcute:close-line" width={16} />
              </IconButton>
            )}
          </Stack>
        );
      })}
    </AnimatePresence>
  );
}

MultiFilePreview.propTypes = {
  files: PropTypes.array,
  onRemove: PropTypes.func,
  onClick: PropTypes.func,
  sx: PropTypes.object,
  thumbnail: PropTypes.bool,
};
